import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch, apiJson } from "../../utils/api.js";
import { getChatPath } from "../../utils/nav.js";
import { fmtDt, userTitle } from "../../utils/users.js";
import { useMe } from "../../hooks/useMe.js";
import { PageReveal } from "./PageReveal.jsx";
import { TransferManagerSheet } from "./TransferManagerSheet.jsx";
import { MessengerBadge } from "./MessengerBadge.jsx";

function buildUserQuery(userId, useTelegramId) {
  const qs = new URLSearchParams({
    user_id: String(userId),
  });
  if (useTelegramId) qs.set("use_telegram_id", "1");
  return qs.toString();
}

function formatAssignedManager(m) {
  if (!m) return "не закреплён";
  const name = (m.display_name || "").trim();
  const code = (m.code || "").trim();
  if (name && code && name !== code) return `${name} · ${code}`;
  return name || code || (m.id != null ? `id ${m.id}` : "—");
}

export function ClientCardModal({ open, onClose, userId, useTelegramId }) {
  const navigate = useNavigate();
  const { isAdmin, isRop, isModerator, canTransferAssignments } = useMe();

  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);

  const [commentsLoading, setCommentsLoading] = useState(false);
  const [comments, setComments] = useState([]);

  const [tagsPopoverOpen, setTagsPopoverOpen] = useState(false);
  const [allTags, setAllTags] = useState([]);
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const currentTagIds = useMemo(() => new Set((user?.tags || []).map((t) => t.id)), [user]);
  const showUsername = isAdmin || isRop || isModerator;

  const [remindPopover, setRemindPopover] = useState(null); // { commentId, dtLocal }

  const [sheetVisible, setSheetVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [nameEditing, setNameEditing] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [transferOpen, setTransferOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setIsExiting(false);
      setSheetVisible(false);
      const id = setTimeout(() => setSheetVisible(true), 20);
      return () => clearTimeout(id);
    } else {
      setSheetVisible(false);
      setIsExiting(true);
      const t = setTimeout(() => setIsExiting(false), 400);
      return () => clearTimeout(t);
    }
  }, [open]);

  async function loadUser() {
    setLoading(true);
    setError(null);
    try {
      const u = await apiJson(`/api/user?${buildUserQuery(userId, useTelegramId)}`);
      setUser(u);
      return u;
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }

  async function loadComments() {
    setCommentsLoading(true);
    try {
      const qs = buildUserQuery(userId, useTelegramId);
      const data = await apiJson(`/api/user/comments?${qs}`);
      setComments(data.items || []);
    } catch {
      setComments([]);
    } finally {
      setCommentsLoading(false);
    }
  }

  useEffect(() => {
    if (!open) return;
    setTagsPopoverOpen(false);
    setRemindPopover(null);
    loadUser().then(() => loadComments());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, userId, useTelegramId]);

  const show = (open || isExiting) && userId;
  if (!show) return null;

  const displayName = user ? userTitle(user) : "…";

  return (
    <div className={`modal ${open && sheetVisible ? "" : "hidden"}`} id="cardModal">
      <div className="modalBackdrop" id="cardBackdrop" onClick={onClose} />
      <div className="modalSheet cardModalSheet">
        {/* Шапку (крестик + заголовок) убрали: закрытие по клику на backdrop */}

        <div className="modalBody card-app" id="cardBody">
          {error ? <div className="pill">Ошибка: {error}</div> : null}

          {user ? (
            <PageReveal ready={!!user}>
            <div className="card-wrap">
              <div className="profile-card">
                <div className="avatar">
                  {String(displayName || "?")
                    .slice(0, 2)
                    .toUpperCase()
                    .replace(/^(.)(.)?/, (_, a, b) => (b ? a + b : a))}
                </div>
                <div className="profile-info">
                  <div className="name-row">
                    {nameEditing ? (
                      <input
                        type="text"
                        className="card-edit-input name-edit-input"
                        value={nameDraft}
                        onChange={(e) => setNameDraft(e.target.value)}
                        onBlur={async () => {
                          const val = nameDraft.trim();
                          try {
                            await apiJson("/api/user", {
                              method: "PATCH",
                              headers: { "Content-Type": "application/json" },
                              body: JSON.stringify({
                                user_id: userId,
                                use_telegram_id: !!useTelegramId,
                                name: val || null,
                              }),
                            });
                            await loadUser();
                          } catch (err) {
                            window.alert(err?.message || "Не удалось сохранить");
                          }
                          setNameEditing(false);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") e.target.blur();
                          if (e.key === "Escape") {
                            setNameDraft(displayName || "");
                            setNameEditing(false);
                          }
                        }}
                        autoFocus
                        placeholder="Имя клиента"
                      />
                    ) : (
                      <div
                        className="name name-editable"
                        title="Нажмите, чтобы изменить"
                        onClick={() => {
                          setNameDraft(displayName || "");
                          setNameEditing(true);
                        }}
                      >
                        {displayName || "—"}
                      </div>
                    )}
                    {user.messenger ? <MessengerBadge messenger={user.messenger} /> : null}
                  </div>

                  <div className="badges">
                    {user.client_kind === "franchise" ? (
                      <span className="badge badgeFranchise" title="Вошёл по ссылке t.me/бот?start=franchise">
                        Франчайзи
                      </span>
                    ) : null}
                    <CopyBadge text={`ID ${user.id}`} copy={String(user.id)} />
                    {showUsername && user.username ? (
                      <CopyBadge text={`@${user.username}`} copy={`@${user.username}`} />
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-title">Основное</div>
                <div className="info-row">
                  <span title="Кому в CRM назначен клиент (поле закрепления)">Менеджер</span>
                  <strong title={user.locked_manager_assignment ? "Клиент пришёл по персональной ссылке" : undefined}>
                    {formatAssignedManager(user.assigned_manager)}
                    {user.locked_manager_assignment ? " · по ссылке" : ""}
                  </strong>
                </div>
                <EditableInfoRow
                  label="Первое обращение"
                  value={user.first_contact_at}
                  displayValue={fmtDt(user.first_contact_at)}
                  inputType="datetime-local"
                  onSave={async (val) => {
                    await apiJson("/api/user", {
                      method: "PATCH",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({
                        user_id: userId,
                        use_telegram_id: !!useTelegramId,
                        first_contact_at: val || new Date().toISOString(),
                      }),
                    });
                    await loadUser();
                  }}
                />
                <EditableInfoRow
                  label="Источник"
                  value={user.source}
                  onSave={async (val) => {
                    await apiJson("/api/user", {
                      method: "PATCH",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({
                        user_id: userId,
                        use_telegram_id: !!useTelegramId,
                        source: val,
                      }),
                    });
                    await loadUser();
                  }}
                />
                <EditableInfoRow
                  label="Телефон"
                  value={user.phone}
                  inputPlaceholder="+7 …"
                  onSave={async (val) => {
                    await apiJson("/api/user", {
                      method: "PATCH",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({
                        user_id: userId,
                        use_telegram_id: !!useTelegramId,
                        phone: val,
                      }),
                    });
                    await loadUser();
                  }}
                />
                <EditableInfoRow
                  label="Email"
                  value={user.email}
                  inputPlaceholder="email@example.com"
                  onSave={async (val) => {
                    await apiJson("/api/user", {
                      method: "PATCH",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({
                        user_id: userId,
                        use_telegram_id: !!useTelegramId,
                        email: val,
                      }),
                    });
                    await loadUser();
                  }}
                />
                <div className="info-row tags-row">
                  <span>Теги</span>
                  <strong className="tags-value">
                    <span className="tags-wrap">
                      {(user.tags || []).slice(0, 2).map((t) => (
                        <span key={String(t.id)} className="badge tag-badge">
                          {t.title}
                        </span>
                      ))}
                    </span>
                    <button
                      type="button"
                      className="tags-edit-btn"
                      title="Изменить теги"
                      onClick={async () => {
                        try {
                          const tagsRes = await apiFetch("/api/tags");
                          if (!tagsRes.ok) throw new Error("Не удалось загрузить теги");
                          const tagsData = await tagsRes.json();
                          const items = tagsData.items || [];
                          setAllTags(items);
                          setSelectedTagIds(items.filter((t) => currentTagIds.has(t.id)).map((t) => t.id));
                          setTagsPopoverOpen(true);
                        } catch (e) {
                          window.alert(String(e));
                        }
                      }}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </button>
                  </strong>
                </div>

                {tagsPopoverOpen ? (
                  <div className="remind-popover tags-popover">
                    <div className="remind-popover-inner">
                      <label>Теги клиента (макс. 2)</label>
                      <div className="tags-check-list">
                        {allTags.map((t) => {
                          const checked = selectedTagIds.includes(t.id);
                          return (
                            <label key={String(t.id)} className="tag-check-item">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => {
                                  const next = e.target.checked
                                    ? [...selectedTagIds, t.id]
                                    : selectedTagIds.filter((x) => x !== t.id);
                                  if (next.length > 2) return;
                                  setSelectedTagIds(next);
                                }}
                              />{" "}
                              {t.title}
                            </label>
                          );
                        })}
                      </div>
                      <div className="remind-popover-actions">
                        <button
                          type="button"
                          className="primary-btn small"
                          onClick={async () => {
                            const selected = selectedTagIds.slice(0, 2);
                            try {
                              await apiJson("/api/user/tags", {
                                method: "POST",
                                headers: { "Content-Type": "application/json" },
                                body: JSON.stringify({
                                  user_id: userId,
                                  use_telegram_id: !!useTelegramId,
                                  tag_ids: selected,
                                }),
                              });
                              setTagsPopoverOpen(false);
                              await loadUser();
                            } catch (err) {
                              window.alert(String(err));
                            }
                          }}
                        >
                          Сохранить
                        </button>
                        <button
                          type="button"
                          className="remind-cancel-btn"
                          onClick={() => setTagsPopoverOpen(false)}
                        >
                          Отмена
                        </button>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="card">
                <div className="card-title">Внутренние комментарии</div>
                <div className="comments-list" id="commentsList">
                  {comments.length ? (
                    comments.map((c) => (
                      <div className="comment" key={String(c.id)}>
                        <div className="comment-text-wrap">
                          <div className="comment-text">{c.text}</div>
                          {c.remind_at ? <div className="comment-remind-at">{fmtDt(c.remind_at)}</div> : null}
                          <button
                            type="button"
                            className={`comment-remind-btn ${c.remind_at ? "has-remind" : ""}`}
                            title={c.remind_at ? `Будильник: ${fmtDt(c.remind_at)}` : "Будильник"}
                            onClick={() => {
                              const now = new Date();
                              const minVal =
                                now.getFullYear() +
                                "-" +
                                String(now.getMonth() + 1).padStart(2, "0") +
                                "-" +
                                String(now.getDate()).padStart(2, "0") +
                                "T" +
                                String(now.getHours()).padStart(2, "0") +
                                ":" +
                                String(now.getMinutes()).padStart(2, "0");
                              setRemindPopover({ commentId: c.id, dtLocal: minVal, minVal });
                            }}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="12" cy="12" r="10" />
                              <polyline points="12 6 12 12 16 14" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="pill">Нет комментариев</div>
                  )}
                </div>

                <CommentComposer
                  onAdd={async (text) => {
                    await apiJson("/api/user/comments", {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({ user_id: userId, use_telegram_id: !!useTelegramId, text }),
                    });
                    await loadComments();
                  }}
                />

                {remindPopover ? (
                  <div className="remind-popover">
                    <div className="remind-popover-inner">
                      <label>Дата и время напоминания</label>
                      <input
                        type="datetime-local"
                        className="remind-datetime"
                        min={remindPopover.minVal}
                        value={remindPopover.dtLocal}
                        onChange={(e) => setRemindPopover((v) => ({ ...v, dtLocal: e.target.value }))}
                      />
                      <div className="remind-popover-actions">
                        <button
                          type="button"
                          className="primary-btn small"
                          onClick={async () => {
                            const v = remindPopover.dtLocal;
                            if (!v) {
                              window.alert("Выберите дату и время");
                              return;
                            }
                            const remindAt = new Date(v).toISOString();
                            try {
                              await apiJson(`/api/user/comments/${remindPopover.commentId}/reminder`, {
                                method: "POST",
                                headers: { "Content-Type": "application/json" },
                                body: JSON.stringify({ remind_at: remindAt }),
                              });
                              setRemindPopover(null);
                              await loadComments();
                            } catch (e) {
                              window.alert(String(e));
                            }
                          }}
                        >
                          Установить
                        </button>
                        <button
                          type="button"
                          className="remind-cancel-btn"
                          onClick={() => setRemindPopover(null)}
                        >
                          Отмена
                        </button>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
            </PageReveal>
          ) : null}
        </div>
        {user ? (
          <div className="cardModalFooter">
            {canTransferAssignments ? (
              <button
                type="button"
                className="cardModalSecondaryBtn"
                onClick={() => setTransferOpen(true)}
              >
                Передать менеджеру
              </button>
            ) : null}
            <button
              type="button"
              className="cardModalChatBtn"
              onClick={() => {
                if (user.telegram_id == null || user.telegram_id <= 0) {
                  window.alert("У этого клиента нет Telegram. Написать в чат невозможно — пользователь не найден в боте.");
                  return;
                }
                onClose?.();
                navigate(getChatPath(user.id, "/clients"));
              }}
            >
              Открыть чат
            </button>
          </div>
        ) : null}
        <TransferManagerSheet
          open={transferOpen}
          onClose={() => setTransferOpen(false)}
          title="Передать клиента"
          hint="Клиент и все его сделки будут закреплены за выбранным менеджером."
          onTransferred={async (toManagerId) => {
            await apiJson("/api/transfer/user", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                user_id: userId,
                use_telegram_id: !!useTelegramId,
                to_manager_id: toManagerId,
              }),
            });
            await loadUser();
          }}
        />
      </div>
    </div>
  );
}

function CopyBadge({ text, copy }) {
  return (
    <span
      className="badge copyable"
      title="Нажмите, чтобы скопировать"
      data-copy={copy}
      onClick={async (e) => {
        e.stopPropagation();
        try {
          await navigator.clipboard.writeText(copy);
        } catch {
          window.alert("Не удалось скопировать");
        }
      }}
    >
      {text}
    </span>
  );
}

function InfoRow({ label, value, copyValue }) {
  const content = (
    <strong
      onClick={async (e) => {
        if (!copyValue) return;
        e.stopPropagation();
        try {
          await navigator.clipboard.writeText(copyValue);
        } catch {
          // ignore
        }
      }}
    >
      {value}
    </strong>
  );
  return (
    <div className="info-row">
      <span>{label}</span>
      {content}
    </div>
  );
}

function toDatetimeLocal(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function EditableInfoRow({ label, value, displayValue: displayValueProp, inputType = "text", inputPlaceholder, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "");
  const [busy, setBusy] = useState(false);

  const displayValue = displayValueProp != null
    ? (String(displayValueProp).trim() || null)
    : (value != null && String(value).trim() !== "" ? String(value).trim() : null);

  const startEdit = () => {
    if (inputType === "datetime-local") {
      setDraft(toDatetimeLocal(value) || "");
    } else {
      setDraft(value != null ? String(value).trim() : "");
    }
    setEditing(true);
  };

  const submit = async () => {
    let valueToSave = typeof draft === "string" ? draft.trim() : draft;
    if (inputType === "datetime-local" && valueToSave) {
      valueToSave = new Date(valueToSave).toISOString();
    } else if (valueToSave === "") {
      valueToSave = null;
    }
    if (busy) return;
    setBusy(true);
    try {
      await onSave(valueToSave);
      setEditing(false);
    } catch (err) {
      window.alert(err?.message || "Не удалось сохранить");
    } finally {
      setBusy(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      submit();
    }
    if (e.key === "Escape") {
      setEditing(false);
      setDraft(value != null ? String(value).trim() : "");
    }
  };

  if (editing) {
    return (
      <div className="info-row info-row-editing">
        <span>{label}</span>
        <span className="info-row-edit-wrap">
          {inputType === "datetime-local" ? (
            <input
              type="datetime-local"
              className="card-edit-input"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={submit}
              onKeyDown={handleKeyDown}
              autoFocus
            />
          ) : (
            <input
              type={inputType}
              className="card-edit-input"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={submit}
              onKeyDown={handleKeyDown}
              placeholder={inputPlaceholder || ""}
              autoFocus
            />
          )}
        </span>
      </div>
    );
  }

  return (
    <div className="info-row info-row-editable">
      <span>{label}</span>
      <strong
        className="info-row-value-trigger"
        onClick={startEdit}
        title="Нажмите, чтобы изменить"
      >
        {displayValue ?? "—"}
      </strong>
    </div>
  );
}

function CommentComposer({ onAdd }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <div className="comment-input-row">
      <input
        id="commentInput"
        type="text"
        placeholder="Добавить комментарий..."
        autoComplete="off"
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      <button
        type="button"
        className="primary-btn small"
        id="commentAddBtn"
        disabled={busy}
        onClick={async () => {
          const t = (text || "").trim();
          if (!t) return;
          setBusy(true);
          try {
            await onAdd?.(t);
            setText("");
          } catch (e) {
            window.alert(String(e));
          } finally {
            setBusy(false);
          }
        }}
      >
        Добавить
      </button>
    </div>
  );
}

